/**
 * KCIS™ Pro — Practitioner Review orchestrator (Sprint 6B4).
 *
 * Loads, in parallel, the active AI summary, current pathway override,
 * review status, and the underlying nutrient results (for engine
 * pathway + escalation flags). Then composes all six Sprint 4 surfaces.
 *
 * Tier gating: Lite is allowed to view AI summaries and append notes /
 * mark review (per RBAC table) but cannot edit AI blocks, override
 * pathway, or suppress escalation. The UI mirrors that — server still
 * enforces 403.
 *
 * Feature flags (gracefully handled):
 *   - KCIS_AI_SUMMARIES_ENABLED        → /ai-summary/regenerate (503 ok)
 *   - KCIS_PRACTITIONER_OVERRIDE_ENABLED → mutations (503 ok)
 * Read endpoints are not flag-gated; we degrade silently if any 503 fires.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, ClipboardList } from "lucide-react";
import { api, adminAuth } from "@/lib/api";
import AiSummaryPanel from "@/components/practitioner/AiSummaryPanel";
import OverrideEditor from "@/components/practitioner/OverrideEditor";
import EscalationControls from "@/components/practitioner/EscalationControls";
import ReviewStatusBar from "@/components/practitioner/ReviewStatusBar";
import AuditTrailDrawer from "@/components/practitioner/AuditTrailDrawer";

function rolePermissions(role) {
  const isSuper = role === "super_admin";
  const isPro = isSuper || role === "practitioner_pro";
  return {
    role,
    canViewSummary: true,
    canRegenerate: isPro,
    canEditBlocks: isPro,
    canOverridePathway: isPro,
    canSuppressEscalation: isPro,
    canElevateEscalation: true, // lite + pro + super
  };
}

export default function PractitionerReview({ clientId }) {
  const [assessmentId, setAssessmentId] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [pathwayOverride, setPathwayOverride] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [auditNonce, setAuditNonce] = useState(0);

  const role = adminAuth.getRole();
  const perms = rolePermissions(role);

  // Resolve the latest Module 1 (Lite) assessment for this client — Sprint 4
  // is keyed by Lite assessment_id (parity with NutrientIndicatorsPanel).
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!clientId) {
        setResolving(false);
        return;
      }
      setResolving(true);
      try {
        const { data } = await api.get(`/clients/${clientId}/assessments`);
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setAssessmentId(data[0].id);
        } else {
          setAssessmentId(null);
        }
      } catch {
        if (!cancelled) setAssessmentId(null);
      } finally {
        if (!cancelled) setResolving(false);
      }
    }
    resolve();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const fetchAll = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const [sumRes, resultsRes, routingRes, escalationRes, reviewRes] = await Promise.all([
        api.get(`/nutrient/${assessmentId}/ai-summary`).catch(() => ({ data: { summary: null } })),
        api.get(`/nutrient/results/${assessmentId}`).catch(() => ({ data: null })),
        api.get(`/nutrient/routing/${assessmentId}`).catch(() => ({ data: null })),
        api.get(`/nutrient/escalation/${assessmentId}`).catch(() => ({ data: null })),
        api.get(`/nutrient/${assessmentId}/review`).catch(() => ({ data: { status: null } })),
      ]);
      setSummary(sumRes?.data?.summary || null);
      setResults({
        ...(resultsRes?.data || {}),
        routing: routingRes?.data || null,
        escalation: escalationRes?.data || null,
      });
      setReviewStatus(reviewRes?.data?.status || null);
      // Pathway override is read from the audit trail OR by hitting the override
      // collection via results — Sprint 4 backend exposes the live override via
      // the patch endpoint only. We surface the most recent override from audit.
      try {
        const audit = await api.get(`/nutrient/${assessmentId}/audit-trail`, {
          params: { page: 1, page_size: 1, action_filter: "override.pathway" },
        });
        const latest = (audit?.data?.items || [])[0];
        if (latest) {
          setPathwayOverride({
            overridden_pathway: latest.after,
            overridden_by: latest.actor_id,
            overridden_at: latest.at,
          });
        } else {
          setPathwayOverride(null);
        }
      } catch {
        setPathwayOverride(null);
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleChanged = useCallback(() => {
    fetchAll();
    setAuditNonce((n) => n + 1);
  }, [fetchAll]);

  if (!clientId) return null;
  if (resolving) {
    return (
      <section
        data-testid="practitioner-review-section"
        className="mb-10 rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-2 text-sm text-slate-500"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Locating Module 1 intake…
      </section>
    );
  }
  if (!assessmentId) {
    return (
      <section
        data-testid="practitioner-review-section"
        className="mb-10 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600"
      >
        Practitioner review unlocks once a Module 1 intake exists for this client.
      </section>
    );
  }

  return (
    <section
      data-testid="practitioner-review-section"
      className="mb-10 space-y-5"
      aria-label="Practitioner review"
    >
      <header className="flex items-center gap-3">
        <ClipboardList className="w-5 h-5 text-purple-900" />
        <div>
          <h2 className="font-display text-2xl text-slate-900">Practitioner review</h2>
          <p className="text-sm text-slate-600">
            AI-assisted synthesis · adjacent overrides · immutable audit trail
          </p>
        </div>
        {loading ? (
          <Loader2
            className="w-4 h-4 animate-spin text-slate-400 ml-2"
            data-testid="practitioner-review-loading"
          />
        ) : null}
      </header>

      <ReviewStatusBar
        assessmentId={assessmentId}
        status={reviewStatus}
        onChanged={handleChanged}
      />

      <AiSummaryPanel
        assessmentId={assessmentId}
        summary={summary}
        loading={loading}
        canRegenerate={perms.canRegenerate}
        canEditBlocks={perms.canEditBlocks}
        onChanged={handleChanged}
      />

      <OverrideEditor
        assessmentId={assessmentId}
        enginePathway={results?.routing?.recommended_funnel}
        currentOverride={pathwayOverride}
        canOverride={perms.canOverridePathway}
        onChanged={handleChanged}
      />

      <EscalationControls
        assessmentId={assessmentId}
        escalation={results?.escalation}
        canSuppress={perms.canSuppressEscalation}
        canElevate={perms.canElevateEscalation}
        onChanged={handleChanged}
      />

      <AuditTrailDrawer key={auditNonce} assessmentId={assessmentId} />
    </section>
  );
}
